import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Printer, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import taxInvoiceService from '../../../services/taxInvoiceService';
import companyService from '../../../services/companyService';
import exportLutService from '../../../services/exportLutService';
import { toast } from 'react-toastify';
import celerisSign from '../../../assets/celeris_sign.png';
import './TaxInvoicePrint.css';

const TaxInvoicePrint = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const downloadRequested = searchParams.get('download') === '1';
  const [invoice, setInvoice] = useState(null);
  const [company, setCompany] = useState(null);
  const [configuredLut, setConfiguredLut] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  // Add print page setup for A4 size and no browser headers/footers
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'print-page-style';
    style.textContent = `
      @media print {
        @page {
          size: A4;
          margin: 0;
        }
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
        html {
          margin: 0 !important;
          padding: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      const existingStyle = document.getElementById('print-page-style');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [invoiceResponse, companyResponse] = await Promise.all([
        taxInvoiceService.getTaxInvoiceById(id),
        companyService.getCompanyDetails()
      ]);

      if (invoiceResponse.success && invoiceResponse.data) {
        setInvoice(invoiceResponse.data);
        const category = invoiceResponse.data.customerCategory;
        const usesLut = invoiceResponse.data.isLUTBond
          || invoiceResponse.data.gstType === 'ZERO_RATED'
          || category === 'Export'
          || category === 'SEZ';
        if (usesLut) {
          try {
            const lutResponse = await exportLutService.getApplicable(invoiceResponse.data.serviceInvoiceDate);
            setConfiguredLut(lutResponse?.data || null);
          } catch (lutError) {
            console.error('Error loading configured LUT:', lutError);
            setConfiguredLut(null);
          }
        }
      } else {
        toast.error('Failed to load invoice data');
      }

      if (companyResponse.success && companyResponse.data) {
        setCompany(companyResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    // Simple number to words converter (can be enhanced)
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 10) return ones[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + numberToWords(num % 100000) : '');
    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 !== 0 ? ' ' + numberToWords(num % 10000000) : '');
  };

  const handlePrint = () => {
    // Wait a bit for any rendering to complete
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const saveInvoicePdf = async (invoiceRecord) => {
    const node = document.querySelector('.invoice-content');
    if (!node) throw new Error('Invoice layout is not ready');

    await Promise.all(
      [...node.querySelectorAll('img')].map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );

    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: node.scrollWidth,
    });
    const image = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imageHeight = (canvas.height * pageWidth) / canvas.width;
    let heightLeft = imageHeight;
    let position = 0;

    pdf.addImage(image, 'PNG', 0, position, pageWidth, imageHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 1) {
      position = heightLeft - imageHeight;
      pdf.addPage();
      pdf.addImage(image, 'PNG', 0, position, pageWidth, imageHeight);
      heightLeft -= pageHeight;
    }

    const safeName = String(invoiceRecord.serviceInvoiceNumber || 'tax-invoice').replace(/[^\w.-]+/g, '_');
    pdf.save(`${safeName}.pdf`);
  };

  useEffect(() => {
    if (!downloadRequested || loading || !invoice) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await saveInvoicePdf(invoice);
        if (!cancelled) {
          toast.success('Invoice PDF downloaded');
          setTimeout(() => window.close(), 800);
        }
      } catch (error) {
        console.error('Error creating invoice PDF:', error);
        if (!cancelled) {
          toast.error('Could not create the invoice PDF. Use the print dialog and choose Save as PDF.');
          window.print();
        }
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [downloadRequested, loading, invoice]);

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return (
      <div className="invoice-print-container">
        <div className="invoice-loading">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-print-container">
        <div className="invoice-error">Invoice not found</div>
      </div>
    );
  }

  const companyAddress = company?.companyBillingAddress;
  const companyContact = company?.contactInfo?.[0];
  const customer = invoice.customer;
  const customerBillingAddress = customer?.customerBillingAddress?.[0];
  const customerShippingAddress = customer?.customerShippingAddress?.[0] || customerBillingAddress; // Fallback to billing if shipping not available
  
  // Determine GST type from invoice's gstType field (set by backend GST calculation logic)
  // gstType can be: 'CGST_SGST', 'CGST_UTGST', 'IGST', 'ZERO_RATED'
  const gstType = invoice.gstType || (invoice.totalIGSTAmount > 0 ? 'IGST' : 'CGST_SGST');
  const isInterState = gstType === 'IGST';
  const isIntraState = gstType === 'CGST_SGST' || gstType === 'CGST_UTGST';
  const isUnionTerritory = gstType === 'CGST_UTGST';
  const isZeroRated = gstType === 'ZERO_RATED';
  const isExportInvoice = invoice.customerCategory === 'Export'
    || invoice.customerCategory === 'SEZ'
    || invoice.isLUTBond === true
    || gstType === 'ZERO_RATED';
  const currencyCode = isExportInvoice ? (invoice.currency || 'USD') : 'INR';
  const currencyWords = currencyCode === 'USD' ? 'US Dollars' : currencyCode === 'EUR' ? 'Euros' : 'Rupees';
  const underLUT = invoice.isLUTBond === true || gstType === 'ZERO_RATED';

  const countryLabel = (country) => {
    if (!country) return '';
    const value = String(country).trim();
    if (/^usa$/i.test(value) || /united states/i.test(value)) return 'United States';
    return value;
  };

  const shortRegion = (state) => {
    if (!state) return '';
    if (/^california$/i.test(String(state).trim())) return 'CA';
    return state;
  };

  const formatPartyLines = (address) => {
    if (!address) return [];
    const locality = [shortRegion(address.state), address.pinCode].filter(Boolean).join(' ');
    const cityLine = [address.city, locality].filter(Boolean).join(', ');
    return [address.line1, address.line2, address.line3, address.line4, cityLine, countryLabel(address.country)].filter(Boolean);
  };
  
  // Get place of supply from invoice or calculate
  const placeOfSupply = isExportInvoice
    ? (countryLabel(invoice.placeOfSupply) || countryLabel(customerBillingAddress?.country) || invoice.placeOfSupply || '')
    : (invoice.placeOfSupply || customerBillingAddress?.state || companyAddress?.state || '');
  
  // Domestic print keeps a fixed 5-row grid. Export print shows the actual lines.
  const originalServiceDetails = invoice.serviceDetails || [];
  const hasMoreThanFiveItems = originalServiceDetails.length > 5;
  const displayServiceDetails = (() => {
    if (isExportInvoice) {
      return originalServiceDetails.length ? originalServiceDetails : [{}];
    }
    const items = originalServiceDetails.slice(0, 5); // Take first 5 if more than 5
    // Pad to exactly 5 rows with empty objects
    while (items.length < 5) {
      items.push({});
    }
    return items;
  })();

  const gstinLabel = (isExportInvoice && !invoice.customerGSTIN) ? 'Not applicable' : (invoice.customerGSTIN || '-');
  const supplyTypeLabel = isExportInvoice
    ? (underLUT ? 'Export under LUT' : (invoice.customerCategory === 'SEZ' ? 'SEZ' : 'Export'))
    : (invoice.customerCategory === 'Export' || invoice.customerCategory === 'SEZ' || invoice.customerCategory === 'Deemed Export' ? 'B2B Export' : 'B2B');
  const lutArn = configuredLut?.lutArn || invoice.lutArn || '';
  const lutFilingDate = configuredLut?.filingDate || invoice.lutFilingDate;
  const lutFinancialYear = configuredLut?.financialYear || invoice.lutFinancialYear || '';

  return (
    <div className="invoice-print-container">
      {/* Print Controls - Hidden when printing */}
      <div className="invoice-print-controls no-print">
        {downloadRequested && (
          <span className="btn-print" style={{ cursor: 'default' }}>Preparing PDF...</span>
        )}
        <button onClick={handlePrint} className="btn-print">
          <Printer size={16} />
          Print
        </button>
          <button onClick={handleClose} className="btn-close">
            <X size={16} />
          </button>
      </div>

      {/* Invoice Content */}
      <div className="invoice-content">
        {/* Compact Header */}
        <div className="invoice-header-compact">
          <div className="invoice-company-info">
            <div className="invoice-company-name-compact">
              {company?.companyName?.toUpperCase() || '-'}
            </div>
            <div className="invoice-company-address-compact">
              {companyAddress ? (
                (() => {
                  const addressParts = [
                    companyAddress.addressLine1,
                    companyAddress.addressLine2,
                    companyAddress.addressLine3,
                    companyAddress.addressLine4,
                    `${companyAddress.city || ''}${companyAddress.city && companyAddress.state ? ', ' : ''}${companyAddress.state || ''} ${companyAddress.pinCode || ''}`.trim(),
                    companyAddress.country || 'India'
                  ].filter(Boolean);
                  return addressParts.length > 0 ? addressParts.join(', ') : '-';
                })()
              ) : '-'}
            </div>
            <div className="invoice-company-contact-compact">
              <span>Phone: +91 89289 81749</span>
              <span>Email: nilesh@celerisventures.ai</span>
            </div>
            <div className="invoice-company-ids-compact">
              <span>CIN: {company?.companyCIN || '-'}</span>
              <span>GSTIN: {company?.GSTIN || '-'}</span>
              {isExportInvoice && (
                <span>IEC: {company?.importExportIECode && company.importExportIECode !== '-' ? company.importExportIECode : '-'}</span>
              )}
            </div>
          </div>
          <div className="invoice-title-compact">Tax Invoice</div>
        </div>

        {/* Three Column Layout: Buyer, Consignee, Document Details */}
        <div className="invoice-details-three-column">
          {/* Buyer's Details [Bill To] */}
          <div className="invoice-detail-column">
            <div className="invoice-column-title">Buyer's Details [Bill To]</div>
            <div className="invoice-column-content">
              <div className="invoice-detail-field">
                <strong>Name:</strong> {invoice.customerName || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Address:</strong> {customerBillingAddress ? (
                  isExportInvoice ? (
                    <div className="invoice-address-lines">
                      {formatPartyLines(customerBillingAddress).map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                  ) : (
                  (() => {
                    const addressParts = [
                      customerBillingAddress.line1,
                      customerBillingAddress.line2,
                      customerBillingAddress.line3,
                      customerBillingAddress.line4,
                      `${customerBillingAddress.city || ''}${customerBillingAddress.city && customerBillingAddress.state ? ', ' : ''}${customerBillingAddress.state || ''} ${customerBillingAddress.pinCode || ''}`.trim(),
                      customerBillingAddress.country || 'India'
                    ].filter(Boolean);
                    return addressParts.length > 0 ? addressParts.join(', ') : '-';
                  })()
                  )
                ) : '-'}
              </div>
              {isExportInvoice && (
                <div className="invoice-detail-field">
                  <strong>Country:</strong> {countryLabel(customerBillingAddress?.country) || placeOfSupply || '-'}
                </div>
              )}
              <div className="invoice-detail-field">
                <strong>GSTIN:</strong> {gstinLabel}
              </div>
              <div className="invoice-detail-field">
                <strong>Mobile No.:</strong> {customer?.customerContactInfo?.[0]?.contactPersonNumber || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>E-mail:</strong> {customer?.customerContactInfo?.[0]?.contactPersonEmail || '-'}
              </div>
            </div>
          </div>

          {/* Consignee Details [Ship To] */}
          <div className="invoice-detail-column">
            <div className="invoice-column-title">{isExportInvoice ? 'Service Recipient' : 'Consignee Details [Ship To]'}</div>
            <div className="invoice-column-content">
              <div className="invoice-detail-field">
                <strong>Name:</strong> {customerShippingAddress ? (invoice.customerName || '-') : '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Address:</strong> {customerShippingAddress ? (
                  isExportInvoice ? (
                    <div className="invoice-address-lines">
                      {formatPartyLines(customerShippingAddress).map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                  ) : (
                  (() => {
                    const addressParts = [
                      customerShippingAddress.line1,
                      customerShippingAddress.line2,
                      customerShippingAddress.line3,
                      customerShippingAddress.line4,
                      `${customerShippingAddress.city || ''}${customerShippingAddress.city && customerShippingAddress.state ? ', ' : ''}${customerShippingAddress.state || ''} ${customerShippingAddress.pinCode || ''}`.trim(),
                      customerShippingAddress.country || 'India'
                    ].filter(Boolean);
                    return addressParts.length > 0 ? addressParts.join(', ') : '-';
                  })()
                  )
                ) : '-'}
              </div>
              {isExportInvoice && (
                <div className="invoice-detail-field">
                  <strong>Country:</strong> {countryLabel(customerShippingAddress?.country) || placeOfSupply || '-'}
                </div>
              )}
              <div className="invoice-detail-field">
                <strong>GSTIN:</strong> {gstinLabel}
              </div>
              <div className="invoice-detail-field">
                <strong>Mobile No.:</strong> {customerShippingAddress?.contactPersonNumber || customer?.customerContactInfo?.[0]?.contactPersonNumber || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>E-mail:</strong> {customer?.customerContactInfo?.[0]?.contactPersonEmail || '-'}
              </div>
            </div>
          </div>

          {/* Document Details */}
          <div className="invoice-detail-column">
            <div className="invoice-column-title">Document Details</div>
            <div className="invoice-column-content">
              <div className="invoice-detail-field">
                <strong>Invoice No.:</strong> {invoice.serviceInvoiceNumber || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Invoice Date:</strong> {formatDate(invoice.serviceInvoiceDate) || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>PO No.:</strong> {invoice.PONo || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>PO Date:</strong> {formatDate(invoice.PODate) || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>{isExportInvoice ? 'Supply Type:' : 'Supply Type Code:'}</strong> {supplyTypeLabel}
              </div>
              <div className="invoice-detail-field">
                <strong>Place of Supply:</strong> {placeOfSupply} {isExportInvoice ? '' : (() => {
                  // Extract state code from customer GSTIN or company GSTIN
                  let stateCode = '';
                  if (invoice.customerGSTIN && invoice.customerGSTIN.length >= 2) {
                    stateCode = invoice.customerGSTIN.substring(0, 2);
                  } else if (company?.GSTIN && company.GSTIN.length >= 2) {
                    stateCode = company.GSTIN.substring(0, 2);
                  }
                  return stateCode ? `[${stateCode}]` : '';
                })()}
              </div>
              {isExportInvoice && (
                <div className="invoice-detail-field">
                  <strong>Due Date:</strong> {formatDate(invoice.dueDate) || '-'}
                </div>
              )}
              <div className="invoice-detail-field">
                <strong>RCM Applicable:</strong> No
              </div>
              <div className="invoice-detail-field">
                <strong>Currency:</strong> {invoice.currency || 'INR'}
              </div>
            </div>
          </div>
        </div>

        {/* Service Details Table */}
        <div className="invoice-services-section">
          <div className="invoice-section-title">Description of Services</div>
          <table className="invoice-services-table">
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Description of Services</th>
                <th>HSN/SAC</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>{isExportInvoice ? `Rate (${currencyCode})` : 'Rate (INR)'}</th>
                {!isExportInvoice && (isInterState ? (
                  <th>IGST (%)</th>
                ) : isUnionTerritory ? (
                  <>
                    <th>CGST (%)</th>
                    <th>UTGST (%)</th>
                  </>
                ) : (
                  <>
                    <th>CGST (%)</th>
                    <th>SGST (%)</th>
                  </>
                ))}
                <th>{isExportInvoice ? `Amount (${currencyCode})` : 'Total Amount (INR)'}</th>
              </tr>
            </thead>
            <tbody>
              {displayServiceDetails.map((service, index) => {
                const isEmpty = !service.serviceDescription && !service.service?.serviceDescription;
                return (
                  <tr key={index} className={isEmpty ? 'empty-row' : ''}>
                    <td>{isEmpty ? '' : index + 1}</td>
                    <td>
                      {isEmpty ? '' : (
                        <>
                          {service.serviceDescription || service.service?.serviceDescription || ''}
                          {service.additionalInfo && <div className="service-reference">Reference: {service.additionalInfo}</div>}
                        </>
                      )}
                    </td>
                    <td>{isEmpty ? '' : (service.SACCode || service.service?.SACCode || '')}</td>
                    <td>{isEmpty ? '' : (service.qty || 0)}</td>
                    <td>{isEmpty ? '' : (service.unit || (isExportInvoice ? 'Service' : 'Nos'))}</td>
                    <td>{isEmpty ? '' : formatCurrency(service.unitRate || 0)}</td>
                    {!isExportInvoice && (isInterState ? (
                      <td>
                        {isEmpty ? '' : Number(service.igstRate || 0).toFixed(2)}
                      </td>
                    ) : isUnionTerritory ? (
                      <>
                        <td>
                          {isEmpty ? '' : Number(service.cgstRate || 0).toFixed(2)}
                        </td>
                        <td>
                          {isEmpty ? '' : Number(service.utgstRate || 0).toFixed(2)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          {isEmpty ? '' : Number(service.cgstRate || 0).toFixed(2)}
                        </td>
                        <td>
                          {isEmpty ? '' : Number(service.sgstRate || 0).toFixed(2)}
                        </td>
                      </>
                    ))}
                    <td>{isEmpty ? '' : formatCurrency(service.lineValue || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Financial Summary */}
        <div className="invoice-summary">
          <div className="invoice-summary-row">
            <span className="invoice-summary-label">{isExportInvoice ? `Total Amount (${currencyCode}):` : 'Total Amount (INR):'}</span>
            <span className="invoice-summary-value">{isExportInvoice ? '' : '₹ '}{formatCurrency(invoice.totalValue || 0)}</span>
          </div>
          {isExportInvoice && underLUT ? (
            <div className="invoice-summary-row">
              <span className="invoice-summary-label">Tax:</span>
              <span className="invoice-summary-value">{formatCurrency(0)}</span>
            </div>
          ) : isZeroRated ? (
            <div className="invoice-summary-row">
              <span className="invoice-summary-label">GST (Zero Rated):</span>
              <span className="invoice-summary-value">₹ {formatCurrency(0)}</span>
            </div>
          ) : isInterState ? (
            <div className="invoice-summary-row">
              <span className="invoice-summary-label">IGST @ {invoice.serviceDetails?.[0]?.igstRate || invoice.GSTDetails?.[0]?.igstRate || 18}%:</span>
              <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalIGSTAmount || 0)}</span>
            </div>
          ) : isUnionTerritory ? (
            <>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">CGST @ {invoice.serviceDetails?.[0]?.cgstRate || invoice.GSTDetails?.[0]?.cgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalCGSTAmount || 0)}</span>
              </div>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">UTGST @ {invoice.serviceDetails?.[0]?.utgstRate || invoice.GSTDetails?.[0]?.utgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalUTGSTAmount || 0)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">CGST @ {invoice.serviceDetails?.[0]?.cgstRate || invoice.GSTDetails?.[0]?.cgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalCGSTAmount || 0)}</span>
              </div>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">SGST @ {invoice.serviceDetails?.[0]?.sgstRate || invoice.GSTDetails?.[0]?.sgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalSGSTAmount || 0)}</span>
              </div>
            </>
          )}
          {Math.abs(invoice.roundOffAmount || 0) >= 0.001 && (
            <div className="invoice-summary-row">
              <span className="invoice-summary-label">Round Off:</span>
              <span className="invoice-summary-value">
                ₹ {formatCurrency(invoice.roundOffAmount || 0)}
              </span>
            </div>
          )}
          <div className="invoice-summary-row invoice-summary-total">
            <span className="invoice-summary-label">{isExportInvoice ? `Total Invoice Amount (${currencyCode}):` : 'Total Invoice Amount:'}</span>
            <span className="invoice-summary-value">{isExportInvoice ? `${currencyCode} ` : '₹ '}{formatCurrency(invoice.totalAmountWithTax || 0)}</span>
          </div>
          <div className="invoice-amount-words-container">
            <div className="invoice-amount-words">
              {isExportInvoice
                ? `Amount in words: ${currencyWords} ${numberToWords(Math.round(invoice.totalAmountWithTax || 0))} Only`
                : `Total Invoice Amount (In words): ${invoice.currency || 'INR'} ${numberToWords(Math.round(invoice.totalAmountWithTax || 0))} Rupees Only`}
            </div>
            <div className="invoice-payment-terms">
              <div className="invoice-payment-terms-label">Payment Terms</div>
              <div className="invoice-payment-terms-value">{invoice.customerPaymentTerms || invoice.paymentTerms || '-'}</div>
            </div>
          </div>
        </div>

        {isExportInvoice && underLUT && (
          <div className="invoice-export-declaration">
            <div className="invoice-export-declaration-title">Export Declaration</div>
            <div className="invoice-export-declaration-text">
              {invoice.customerCategory === 'SEZ'
                ? 'SUPPLY TO SEZ UNDER BOND OR LETTER OF UNDERTAKING WITHOUT PAYMENT OF INTEGRATED TAX'
                : 'SUPPLY MEANT FOR EXPORT UNDER BOND OR LETTER OF UNDERTAKING WITHOUT PAYMENT OF INTEGRATED TAX'}
            </div>
            <div className="invoice-lut-meta">
              <span><strong>LUT ARN:</strong> {lutArn || '-'}</span>
              <span><strong>Filing Date:</strong> {formatDate(lutFilingDate) || '-'}</span>
              <span><strong>Financial Year:</strong> {lutFinancialYear || '-'}</span>
            </div>
          </div>
        )}

        {/* Spacer for print layout */}
        <div className="invoice-print-spacer"></div>

        {/* Bank Details and Authorized Signatory */}
        {company && (
          <div className={`invoice-bank-details ${hasMoreThanFiveItems ? 'page-break-before' : ''}`}>
            <div className="invoice-bank-signatory-container">
              <div className="invoice-bank-section">
                <div className="invoice-section-title">Bank Details:</div>
                <div className="invoice-bank-info">
                  <div><strong>Beneficiary:</strong> {company?.companyName || '-'}</div>
                  <div><strong>Bank Name:</strong> {company?.companyBankName || '-'}</div>
                  <div><strong>Branch:</strong> {company?.companyBankBranch || '-'}</div>
                  <div><strong>Account No.:</strong> {company?.companyAccountNumber || '-'}</div>
                  <div><strong>IFS Code:</strong> {company?.companyBankIFSCCode || '-'}</div>
                  {isExportInvoice && (
                    <div><strong>SWIFT / BIC:</strong> {company?.companyBankSWIFT || '-'}</div>
                  )}
                </div>
              </div>
              <div className="invoice-signatory-section">
                <div className="invoice-signatory-for">For {company?.companyName || 'Company'}</div>
                  <img src={celerisSign} alt="Signature" style={{ marginRight: '25px', maxWidth: '150px', maxHeight: '80px', objectFit: 'contain', display: 'block' }} />
                <div className="invoice-signatory-label">Authorized Signatory</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={`invoice-footer ${hasMoreThanFiveItems ? 'page-break-before' : ''}`}>
          {isExportInvoice ? (
            <>
              <div className="invoice-export-notes">
                <div>Payment reference: {invoice.serviceInvoiceNumber || '-'}</div>
                <div>All amounts are in {currencyCode}.</div>
                <div><strong>Taxes:</strong> {underLUT ? 'Export of services under LUT.' : 'Export of services.'}</div>
              </div>
            </>
          ) : (
            <div className="invoice-footer-declaration">
              <div className="invoice-footer-declaration-title">Declaration:</div>
              <div className="invoice-footer-disclaimer">
                We certify that the particulars given above are true and correct and the amount indicated represents the price actually charged and that there is no flow of additional consideration directly or indirectly from the buyer.
              </div>
            </div>
          )}
          <div className="invoice-footer-computer-generated">
            This is computer-generated invoice no signature required
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxInvoicePrint;

